import React, { useEffect } from "react";
import { Tabs } from "flowbite-react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Hero from "./Support/components/Hero";
import GoogleGuide from "./Support/components/GoogleGuide";
import AppleGuide from "./Support/components/AppleGuide";
import AppleSearchAdsGuide from "./Support/components/AppleSearchAdsGuide";
import QuickLinks from "./Support/components/QuickLinks";
import FAQ from "./Support/components/FAQ";
import ContactForm from "./Support/components/ContactForm";
import SupportTicketsList from "./Support/components/SupportTicketsList";

const Support: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // Determine target section from hash, query param, or navigation state
    let targetId = "";

    if (location.hash) {
      targetId = location.hash.replace("#", "");
    }

    if (!targetId && location.search) {
      const params = new URLSearchParams(location.search);
      targetId = params.get("section") || "";
    }

    const stateAny = location.state as any;
    if (!targetId && stateAny) {
      targetId = stateAny.section || stateAny.scrollTo || "";
    }

    if (targetId) {
      const el = document.getElementById(targetId);
      if (el) {
        // Smooth scroll with offset accounting for sticky headers within the support page
        const getScrollParent = (node: HTMLElement | null): HTMLElement => {
          let current: HTMLElement | null = node?.parentElement || null;
          while (current) {
            const style = window.getComputedStyle(current);
            const overflowY = style.overflowY;
            const isScrollable =
              (overflowY === "auto" || overflowY === "scroll") &&
              current.scrollHeight > current.clientHeight;
            if (isScrollable) return current;
            current = current.parentElement;
          }
          return document.scrollingElement as HTMLElement;
        };

        const scrollParent = getScrollParent(el);

        // Measure sticky tablist inside support page (defined in index.css under .support-tabs [role="tablist"]).
        const tablist = document.querySelector(
          '.support-tabs [role="tablist"]'
        ) as HTMLElement | null;

        // Base offset: height of the sticky tablist, plus a small extra gap to clear shadows/padding.
        const tablistHeight = tablist?.offsetHeight || 0;
        // index.css sets top: -8px on the tablist; add a small compensation.
        const compensation = 8;
        const extraGap = 8; // visual breathing room
        const offset = tablistHeight + compensation + extraGap;

        // Compute target position relative to the scroll container
        const parentRect = scrollParent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const targetTop =
          elRect.top - parentRect.top + scrollParent.scrollTop - offset;

        // Ensure the scroll happens after paint for consistent measurements
        requestAnimationFrame(() => {
          scrollParent.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "smooth",
          });
        });
      }
    }
  }, [location.hash, location.search, location.state]);

  return (
    <Tabs
      aria-label="Support Tabs"
      style="underline"
      theme={{
        tabpanel: "!p-0",
      }}
    >
      <Tabs.Item title="Integration Guides" active>
        <div className="space-y-4 max-h-[calc(100vh-142px)] overflow-y-auto relative">
          <Hero />

          {/* Nested tabs for integration guides */}
          <Tabs
            className="sticky top-0 bg-white dark:bg-gray-800 pb-2"
            aria-label="Integration Platform Tabs"
            style="pills"
            theme={{
              tabpanel: "!p-0 ",
            }}
          >
            <Tabs.Item title="Google Play Store" active>
              <GoogleGuide />
            </Tabs.Item>
            <Tabs.Item title="Apple App Store">
              <AppleGuide />
            </Tabs.Item>
            <Tabs.Item title="Apple Search Ads">
              <AppleSearchAdsGuide />
            </Tabs.Item>
          </Tabs>
        </div>
      </Tabs.Item>
      <Tabs.Item title="Help Center">
        <div className="space-y-3">
          <QuickLinks />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <FAQ />
            <ContactForm />
          </div>
        </div>
      </Tabs.Item>
      <Tabs.Item title={t("support.tabs.tickets")}>
        <SupportTicketsList />
      </Tabs.Item>
    </Tabs>
  );
};

export default Support;
